import React from 'react';
import type { ToastMessageProps } from '@archipro-design/aria';
import { ToastMessage } from '@archipro-design/aria';
import {
    Button,
    Caption,
    Dialog,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import type { EnquiryFormShape } from './SampleEnquiryForm';
import {
    enquiryFormValidator,
    enquiryFormValidatorWithAddress,
} from './SampleEnquiryForm';
import { TimesLight } from '@archipro-design/icons';
import * as S from './SampleEnquiryPopup.style';
import SampleEnquiryForm from './SampleEnquiryForm';
import { useFetcher } from '@remix-run/react';
import { useUser } from '~/modules/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTracker } from '@archipro-website/tracker';
import type { CreateSampleEnquiryResponse } from '~/routes/remix-api.enquiry.create-sample-enquiry';
import { useLatest } from 'ahooks';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';

interface Props {
    data: {
        product: ProductPageLoaderData['product'];
    };
    open: boolean;
    onClose(): void;
}

const SampleEnquiryPopup = ({ data: { product }, open, onClose }: Props) => {
    const title = product.SampleTitle || 'Sample request';
    const modalText = product.SampleText;
    const { css } = useStyles();
    const [toastState, setToastState] =
        React.useState<ToastMessageProps | null>(null);
    const onCloseRef = useLatest(onClose);
    const formRef = React.useRef<HTMLFormElement>(null);

    const user = useUser();
    const tracker = useTracker();

    const fetcher = useFetcher<CreateSampleEnquiryResponse>();
    const apiURL = '/remix-api/enquiry/create-sample-enquiry';

    const onSubmitError = () => {
        setToastState({
            type: 'error',
            message: 'Something went wrong. Please try again later.',
            onClose: () => {},
        });
    };

    const onSubmit = () => {
        try {
            const formData = new FormData(formRef.current!);

            formData.append('ProductID', product.ID.toString());
            formData.append(
                'ProfessionalID',
                product.Professional.ID.toString(),
            );

            fetcher.submit(formData, {
                method: 'POST',
                action: apiURL,
            });

            tracker.log('RequestSampleSubmit', {
                label: product.Professional.Title,
                url: new URL(window.location.href),
                data: {},
            });
        } catch {
            onSubmitError();
        }
    };

    const defaultValues =
        user?.__typename === 'Me'
            ? {
                  Name: `${user.FirstName} ${user.LastName}`.trim(),
                  Email: user.Email,
                  Phone: user.Phone,
                  Company: user.Company,
              }
            : {};
    const formValidator = product.SamplesAddressRequired
        ? enquiryFormValidatorWithAddress
        : enquiryFormValidator;

    const { control, handleSubmit } = useForm<EnquiryFormShape>({
        defaultValues,
        resolver: zodResolver(formValidator),
        mode: 'onTouched',
    });

    React.useEffect(() => {
        if (fetcher.data === true) {
            setToastState({
                type: 'success',
                message: 'Your request has been sent successfully.',
                onClose: () => {
                    setToastState(null);
                },
            });
            onCloseRef.current();
        } else if (fetcher.data === false) onSubmitError();
    }, [fetcher, onCloseRef]);

    return (
        <>
            <Dialog
                className={css(S.SampleDialog)}
                open={open}
                header={{
                    content: <Caption variant="01">{title}</Caption>,
                }}
                headerAction={{
                    onClick: onClose,
                    content: <TimesLight />,
                    variant: 'icon',
                }}
                content={
                    <fetcher.Form
                        id="sampleEnquiryForm"
                        method="post"
                        onSubmit={handleSubmit(() => onSubmit())}
                        action={apiURL}
                        ref={formRef}
                    >
                        {modalText && (
                            <Caption variant="03">
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: modalText,
                                    }}
                                />
                            </Caption>
                        )}
                        <SampleEnquiryForm data={product} control={control} />
                    </fetcher.Form>
                }
                onCancel={onClose}
                onConfirm={onClose}
                footer={
                    <div className={css(S.ButtonRow)}>
                        <Button
                            size={18}
                            color="grey"
                            disabled={fetcher.state === 'submitting'}
                        >
                            Cancel
                        </Button>
                        <Button
                            size={18}
                            loading={fetcher.state === 'submitting'}
                            onClick={handleSubmit(() => onSubmit())}
                        >
                            {product.SampleSubmitText || 'Submit'}
                        </Button>
                    </div>
                }
                variables={{
                    rootWidth: pxToRem(500),
                }}
            />
            {toastState && (
                <ToastMessage
                    {...toastState}
                    onClose={() => setToastState(null)}
                />
            )}
        </>
    );
};

export default SampleEnquiryPopup;
